import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { Endpoints } from "../../Global/Endpoints";
import "react-toastify/dist/ReactToastify.css";

const marketplaceIcon = "/win/images/marketplace.png";

const Benefit_MarketPlaceDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const contract = useSelector((state) => state.userData);
  const token = localStorage.getItem("AccessToken");
  const marketplaceItem = location.state?.marketplaceItem || {};

  const requestedTitle = marketplaceItem.Title || "";
  const language = contract?.userData?.Language || "English";
  const requestedContract = contract?.userData?.ContractIdentified || contract?.userData?.Contract || "";
  const emailId = contract?.userData?.EmailID || "";
  const ssoData = useSelector((state) => state.sso.ssoUserData);
  const isHighmarkPortal = ssoData?.Portal === "Highmark";
  const deviceType = isHighmarkPortal ? "HighmarkPortal" : "PatientPortal";

  const [loading, setLoading] = useState(true);
  const [marketplaceDetail, setMarketplaceDetail] = useState(null);
  const [showUnavailableMessage, setShowUnavailableMessage] = useState(false);

  const requestParams = useMemo(
    () => ({
      EmailID: emailId,
      Language: language,
      Title: requestedTitle,
      Contract: requestedContract,
      DeviceType: deviceType,
      EmployerCovered: marketplaceItem?.EmployerCovered,
    }),
    [requestedContract, deviceType, emailId, language, requestedTitle]
  );

  useEffect(() => {
    const fetchMarketplaceDetails = async () => {
      if (!token || !emailId || !requestedTitle) {
        setMarketplaceDetail({
          Title: requestedTitle,
          ImagePath: "",
          Description: "",
          Features: [],
          RedirectLink: "",
          RedirectScreen: "",
          EmployerCoveredText: "",
        });
        setShowUnavailableMessage(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      setShowUnavailableMessage(false);

      try {
        const response = await axios.post(
          Endpoints.LoadMarketPlaceDetails,
          `_Params=${encodeURIComponent(JSON.stringify(requestParams))}`,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const detail = response?.data;
        const normalizedDetail = Array.isArray(detail) ? detail[0] : detail;

        if (!normalizedDetail || !normalizedDetail.Title) {
          setMarketplaceDetail({
            Title: requestedTitle,
            ImagePath: "",
            Description: "",
            Features: [],
            RedirectLink: "",
            RedirectScreen: "",
            EmployerCoveredText: "",
          });
          setShowUnavailableMessage(true);
          return;
        }

        setMarketplaceDetail(normalizedDetail);
      } catch (error) {
        setMarketplaceDetail({
          Title: requestedTitle,
          ImagePath: "",
          Description: "",
          Features: [],
          RedirectLink: "",
          RedirectScreen: "",
          EmployerCoveredText: "",
        });
        setShowUnavailableMessage(true);
      } finally {
        setLoading(false);
      }
    };

    fetchMarketplaceDetails();
  }, [requestParams, emailId, requestedTitle, token]);

  const getFeatureItems = (features) => {
    if (!Array.isArray(features)) {
      return [];
    }

    return features.filter(Boolean);
  };

  const getFeatureHtml = (features) => {
    if (typeof features !== "string") {
      return "";
    }

    return features.trim();
  };

  const getEmployerCoveredClassName = (employerCovered) => {
    const normalizedEmployerCovered = employerCovered?.trim().toLowerCase();

    if (normalizedEmployerCovered === "yes") {
      return "employer-covered-pink";
    }

    if (normalizedEmployerCovered === "no") {
      return "employer-covered-yellow";
    }

    return "employer-covered-bordered";
  };

  const handleBackClick = () => {
    navigate(location.state?.from || "/benefits-winmarketplace");
  };

  const logMarketplaceActivity = async (activity) => {
    if (!emailId || !token) {
      return;
    }

    await axios.post(
      Endpoints.CallRecoderLog,
      `_Params=${encodeURIComponent(
        JSON.stringify({
          EmailID: emailId,
          Activity: activity,
          DeviceType: deviceType,
        })
      )}`,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      }
    );
  };

  const getAndOpenMiraLink = async (serviceDescription, miraLink) => {
    const params = {
      EmailID: emailId,
      DeviceType: deviceType,
      Contract: requestedContract,
      MemberID: contract?.userData?.MemberID || "",
      ServiceDescription: serviceDescription,
      MiraLink: miraLink,
    };

    const response = await axios.post(
      Endpoints.GetMiraLink,
      `_Params=${encodeURIComponent(JSON.stringify(params))}`,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response?.data && response.data !== "") {
      window.open(response.data, "_blank", "noopener,noreferrer");
    }
  };

  const oidcIntegration = async (vendor) => {
    const params = {
      emailId: emailId,
      vendor: vendor,
    };

    const response = await axios.post(
      Endpoints.OidcIntegration,
      `_Params=${encodeURIComponent(JSON.stringify(params))}`,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response?.data?.trim?.() || "";
  };

  const getPosterityRedirectLink = async () => {
    const params = {
      MemberID: contract?.userData?.MemberID || "",
      ProviderType: "At Home Sperm Testing Kit",
      Contract: requestedContract,
      DeviceType: deviceType,
      EmailID: emailId,
      NonPhysicianProvider: "",
    };

    const response = await axios.post(
      Endpoints.LoadNonPhysicianProviders,
      `_Params=${encodeURIComponent(JSON.stringify(params))}`,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const providerDetails = Array.isArray(response?.data?.NonPhysicianProviders)
      ? response.data.NonPhysicianProviders
      : [];

    const sortedProviders = [...providerDetails].sort(
      (a, b) => (b?.IsFavourite || "").localeCompare(a?.IsFavourite || "")
    );

    return sortedProviders[0]?.redirect_url || "";
  };

  const handleGetStartedClick = async () => {
    const redirectLink = marketplaceDetail?.RedirectLink?.trim();
    const redirectScreen = marketplaceDetail?.RedirectScreen?.trim();
    const selectedTitle = marketplaceDetail?.Title || requestedTitle;
    const buildMarketplaceActivity = (title, resolvedRedirectLink) =>
      resolvedRedirectLink
        ? `WIN Marketplace-${title}-${resolvedRedirectLink}`
        : `WIN Marketplace-${title}`;
    const logMarketplaceActivitySafely = async (resolvedRedirectLink) => {
      try {
        await logMarketplaceActivity(buildMarketplaceActivity(selectedTitle, resolvedRedirectLink));
      } catch (error) {
        console.log("Error logging callRecoder");
      }
    };

    if (redirectLink) {
      try {
        await logMarketplaceActivitySafely(redirectLink);
      } finally {
        window.open(redirectLink, "_blank", "noopener,noreferrer");
      }
      return;
    }

    if (!redirectScreen) {
      return;
    }

    switch (redirectScreen) {
      case "Mira":
        await logMarketplaceActivitySafely();
        await getAndOpenMiraLink("Preconception", "");
        return;
      case "MiraMenopause":
        await logMarketplaceActivitySafely();
        await getAndOpenMiraLink("Menopause", "");
        return;
      case "BlueBerry":
        await logMarketplaceActivitySafely();
        navigate("/pediatric-care");
        return;
      case "Bend": {
        const oidcRedirectLink = await oidcIntegration("Bend");
        await logMarketplaceActivitySafely(oidcRedirectLink);
        if (oidcRedirectLink) {
          window.open(oidcRedirectLink, "_blank", "noopener,noreferrer");
        }
        return;
      }
      case "FamilyFirst": {
        const oidcRedirectLink = await oidcIntegration("FamilyFirst");
        await logMarketplaceActivitySafely(oidcRedirectLink);
        if (oidcRedirectLink) {
          window.open(oidcRedirectLink, "_blank", "noopener,noreferrer");
        }
        return;
      }
      case "Posterity": {
        const webLink = await getPosterityRedirectLink();
        await logMarketplaceActivitySafely(webLink);
        if (webLink) {
          window.open(webLink, "_blank", "noopener,noreferrer");
        }
        return;
      }
      case "MilkStork": {
        const oidcRedirectLink = await oidcIntegration("Milkstork");
        await logMarketplaceActivitySafely(oidcRedirectLink);
        if (oidcRedirectLink) {
          window.open(oidcRedirectLink, "_blank", "noopener,noreferrer");
        }
        return;
      }
      case "ShapeyourSuccess":
        await logMarketplaceActivitySafely();
        navigate("/return-to-work-support");
        return;

      default:
        return;
    }
  };

  const hasGetStartedAction = Boolean(
    marketplaceDetail?.RedirectLink?.trim() || marketplaceDetail?.RedirectScreen?.trim()
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading"></div>
        <h2 className="loading-text">{t("loading")}</h2>
      </div>
    );
  }

  return (
    <>
      <div className="col-md-12 mt-4 d-flex inner-page-heading-wrapper">
        <button className="win-back-btn me-1" onClick={handleBackClick}>
          <span>
            <i className="bi bi-chevron-left"></i> {t("back")}
          </span>
        </button>
        <h2 className="page-heading">{isHighmarkPortal ? "Fertility and Family Building Marketplace" : t("winmarketplace")}</h2>
      </div>
      <div className="col-12 col-md-12 mb-5">
        <div className="marketplace-detail-header d-lg-none d-flex gap-3 align-items-center justify-content-center">
          <img src={marketplaceIcon} alt="WIN Marketplace" className="image" />
          <h3 style={{ marginBottom: 0 }}>{isHighmarkPortal ? "Fertility and Family Building Marketplace" : "WIN Marketplace"}</h3>
        </div>
        {showUnavailableMessage ? (
          <div
            className="d-flex align-items-center justify-content-center text-center"
            style={{ minHeight: "50vh" }}
          >
            <p className="mb-0">
              Detailed information about {marketplaceDetail?.Title || requestedTitle} is currently unavailable.
              Please try again later.
            </p>
          </div>
        ) : (
          <div className="marketplace-detail">
            <div className="marketplace-detail-body">
              <div className="marketplace-detail-media">
                <div className="image-wrapper">
                  {marketplaceDetail?.ImagePath ? (
                    <img
                      src={marketplaceDetail.ImagePath}
                      className="image"
                      alt={marketplaceDetail.Title || requestedTitle || "WIN Marketplace"}
                    />
                  ) : (
                    <img
                      src={marketplaceIcon}
                      className="image"
                      alt={marketplaceDetail?.Title || requestedTitle || "WIN Marketplace"}
                    />
                  )}
                </div>
                {marketplaceDetail?.EmployerCoveredText && (
                  <div
                    className={`marketplace-detail-employer-text mb-4 text-center ${getEmployerCoveredClassName(
                      marketplaceItem?.EmployerCovered
                    )}`}
                  >
                    <div dangerouslySetInnerHTML={{ __html: marketplaceDetail.EmployerCoveredText }} />
                  </div>
                )}
              </div>
              <div className="marketplace-detail-panel">
                <div className="marketplace-detail-description">
                  {marketplaceDetail?.Description && (
                    <div dangerouslySetInnerHTML={{ __html: marketplaceDetail.Description }} />
                  )}
                </div>
                <div className="marketplace-detail-content">
                  {getFeatureHtml(marketplaceDetail?.Features) ? (
                    <div
                      className="marketplace-detail-bullets"
                      dangerouslySetInnerHTML={{ __html: getFeatureHtml(marketplaceDetail?.Features) }}
                    />
                  ) : getFeatureItems(marketplaceDetail?.Features).length > 0 ? (
                    <ul className="marketplace-detail-bullets">
                      {getFeatureItems(marketplaceDetail?.Features).map((item, index) => (
                        <li
                          key={`${item}-${index}`}
                          dangerouslySetInnerHTML={{ __html: typeof item === "string" ? item : String(item) }}
                        />
                      ))}
                    </ul>
                  ) : null}
                  {hasGetStartedAction && (
                    <button
                      type="button"
                      className="win-btn-secondary-w-100 marketplace-detail-order-button mb-3"
                      style={{
                        width: "auto",
                        minWidth: "300px",
                        display: "block",
                        marginLeft: "auto",
                        marginRight: "auto",
                      }}
                      onClick={handleGetStartedClick}
                    >
                      {t("getstarted")} &gt;
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Benefit_MarketPlaceDetail;
